import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// ─── Uterine Fibroids ──────────────────────────────────────────────

const fibroids: ContentBlock<"condition"> = {
  id: makeId("dx-uterine-fibroids"),
  kind: "condition",
  tags: ["her", "evidence-based"],
  title: "Uterine fibroids",
  subtitle: "Non-cancerous growths in or around the uterus",
  body: {
    conditionName: "Uterine fibroids (leiomyomas)",
    category: "structural",
    description:
      "Fibroids are benign growths of smooth muscle in the uterus. Their impact on fertility depends largely on location: submucosal fibroids (inside the uterine cavity) can significantly impair implantation, while subserosal fibroids (on the outer surface) usually don't affect fertility. Intramural fibroids (within the wall) have variable impact depending on size and proximity to the cavity.",
    symptoms: [
      "Heavy or prolonged periods",
      "Pelvic pain or pressure",
      "Frequent urination",
      "Difficulty conceiving or recurrent miscarriage",
      "Many fibroids are asymptomatic",
    ],
    howDiagnosed:
      "Initially detected by pelvic ultrasound. A saline infusion sonogram (SIS) or hysteroscopy can determine whether fibroids distort the uterine cavity. MRI provides the most detailed mapping of fibroid size, number, and location.",
    impactOnFertility:
      "Submucosal fibroids reduce implantation rates by approximately 50% and should be removed before IVF. Large intramural fibroids (> 4–5 cm) may also impair outcomes. Subserosal fibroids generally don't affect fertility.",
    prevalence:
      "Extremely common — up to 70–80% of women by age 50, though many are asymptomatic. Present in 5–10% of infertile women.",
    treatmentOptions: [
      "Hysteroscopic myomectomy (for submucosal fibroids — minimally invasive)",
      "Laparoscopic or open myomectomy (for larger or multiple fibroids)",
      "Watchful waiting if fibroids don't affect the cavity",
      "IVF after surgical correction",
    ],
    prognosis:
      "After removal of cavity-distorting fibroids, fertility outcomes improve significantly. IVF success rates normalize once the uterine cavity is restored. Fibroids can recur, so timing of treatment relative to fertility plans matters.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "ACOG Practice Bulletin No. 228: Management of Symptomatic Uterine Leiomyomas",
      authors: "American College of Obstetricians and Gynecologists",
      journal: "Obstetrics & Gynecology",
      year: 2021,
      url: "https://journals.lww.com/greenjournal/fulltext/2021/06000/management_of_symptomatic_uterine_leiomyomas__acog.36.aspx",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Removal of myomas in asymptomatic patients to improve fertility and/or reduce miscarriage rate: a guideline",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2017,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(fibroids);

// ─── Tubal Factor ───────────────────────────────────────────────────

const tubalFactor: ContentBlock<"condition"> = {
  id: makeId("dx-tubal-factor"),
  kind: "condition",
  tags: ["her", "evidence-based"],
  title: "Tubal factor infertility",
  subtitle: "Blocked or damaged fallopian tubes",
  body: {
    conditionName: "Tubal factor infertility",
    category: "structural",
    description:
      "Tubal factor infertility occurs when one or both fallopian tubes are blocked, damaged, or absent, preventing the egg and sperm from meeting. Common causes include pelvic inflammatory disease (PID), previous ectopic pregnancy, endometriosis, and prior pelvic surgery. Hydrosalpinx (fluid-filled tubes) is particularly damaging to fertility.",
    symptoms: [
      "Usually no symptoms unless hydrosalpinx is present",
      "History of pelvic infection",
      "Difficulty conceiving",
      "Occasionally pelvic pain",
    ],
    howDiagnosed:
      "Hysterosalpingogram (HSG) is the standard screening test — dye is injected through the cervix and X-rays show whether tubes are open. Laparoscopy with chromopertubation provides definitive diagnosis. Ultrasound can detect hydrosalpinx.",
    impactOnFertility:
      "Blocked tubes prevent natural conception and IUI. Hydrosalpinx fluid can leak into the uterus and reduce IVF success by 50% — removal or clipping of hydrosalpinx before IVF is strongly recommended.",
    prevalence: "Accounts for 25–35% of female infertility.",
    treatmentOptions: [
      "IVF (bypasses the tubes entirely — primary treatment)",
      "Salpingectomy for hydrosalpinx before IVF",
      "Tubal surgery/repair (selected cases with minimal damage)",
      "Proximal tubal cannulation (for proximal blockages)",
    ],
    prognosis:
      "IVF success rates are excellent once hydrosalpinx is addressed. For mild tubal damage, surgical repair can restore natural fertility in some cases, though IVF is generally preferred for efficiency.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "Role of tubal surgery in the era of assisted reproductive technology: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2021,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(tubalFactor);

// ─── Uterine Polyps ─────────────────────────────────────────────────

const polyps: ContentBlock<"condition"> = {
  id: makeId("dx-uterine-polyps"),
  kind: "condition",
  tags: ["her", "evidence-based"],
  title: "Uterine polyps",
  subtitle: "Benign growths on the uterine lining",
  body: {
    conditionName: "Endometrial polyps",
    category: "structural",
    description:
      "Endometrial polyps are localized overgrowths of the uterine lining (endometrium) that project into the uterine cavity. They're usually benign but can interfere with embryo implantation by disrupting the endometrial surface and local inflammatory environment.",
    symptoms: [
      "Irregular or intermenstrual bleeding",
      "Heavy periods",
      "Post-menopausal bleeding",
      "Often incidental finding on ultrasound",
      "Difficulty conceiving",
    ],
    howDiagnosed:
      "Suspected on transvaginal ultrasound, confirmed by saline infusion sonogram (SIS) or hysteroscopy. Hysteroscopy allows simultaneous diagnosis and removal with tissue sent for pathology.",
    impactOnFertility:
      "Polyps larger than 1–2 cm are associated with reduced implantation rates. Removal of polyps before IVF or IUI has been shown to improve pregnancy rates. Smaller polyps may have less impact.",
    prevalence: "Found in 10–30% of women undergoing fertility evaluation.",
    treatmentOptions: [
      "Hysteroscopic polypectomy (simple outpatient procedure)",
      "Proceed with fertility treatment after removal",
      "Small polyps may be monitored without removal",
    ],
    prognosis:
      "Excellent after removal. Polypectomy is a straightforward procedure with quick recovery. Natural and assisted conception rates improve after removal, and recurrence is relatively low.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "AAGL Practice Guidelines for the Diagnosis and Management of Endometrial Polyps",
      authors: "AAGL",
      journal: "Journal of Minimally Invasive Gynecology",
      year: 2012,
      url: "https://pubmed.ncbi.nlm.nih.gov/22196255/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(polyps);
